import React from "react";
import { Check, Loader2 } from "lucide-react";

/**
 * SubcategoryFiltersPanel
 *
 * Rendered inside the search-bar pill popover when the MOVE category and a specific
 * subcategory are selected. Shows the subcategory's filter groups (e.g. "Vehicle Type")
 * with their options as multi-select checkbox rows. Selected option ids are surfaced via
 * onToggleOption and ultimately sent to the space-search API as filterOptionIds=10,11,201.
 *
 * Props:
 * - filters: Array<{ id, name, options: Array<{ id, name, value, imageType, image, sortOrder }> }>
 * - selectedOptionIds: number[]
 * - onToggleOption: (optionId: number) => void
 * - isLoading: boolean
 * - isError: boolean
 */

// Render an option's icon safely. `image` may be raw SVG markup (imageType "SVG") or a URL
// (imageType "URL"). Raw SVG is rendered via an <img> data URI — NOT dangerouslySetInnerHTML —
// so untrusted markup cannot execute scripts.
const OptionIcon = ({ option }) => {
  if (!option?.image) return null;
  const src = option.imageType === "URL" ? option.image : `data:image/svg+xml;utf8,${encodeURIComponent(option.image)}`;
  return <img src={src} alt="" aria-hidden="true" className="h-5 w-5 object-contain flex-shrink-0" />;
};

const SubcategoryFiltersPanel = ({
  filters = [],
  selectedOptionIds = [],
  onToggleOption,
  isLoading = false,
  isError = false,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading filters…
      </div>
    );
  }

  if (isError) {
    return <div className="px-3 py-3 text-sm text-gray-500">Couldn't load filters.</div>;
  }

  const validFilters = (filters || []).filter(f => Array.isArray(f?.options) && f.options.length > 0);

  if (validFilters.length === 0) {
    return <div className="px-3 py-3 text-sm text-gray-500">No filters available.</div>;
  }

  return (
    <div className="py-1 max-h-80 overflow-y-auto">
      {validFilters.map(filter => {
        const options = [...filter.options].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        return (
          <div key={filter.id} className="py-1">
            <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{filter.name}</div>
            {options.map(option => {
              // Coerce both sides: selected ids may hydrate from storage as numbers while an
              // option.id could arrive as a string; compare numerically to stay consistent.
              const isSelected = selectedOptionIds.some(id => Number(id) === Number(option.id));
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => onToggleOption && onToggleOption(option.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${
                    isSelected ? "bg-blue-50" : ""
                  }`}
                >
                  <span
                    className={`flex h-[18px] w-[18px] items-center justify-center rounded border-2 flex-shrink-0 ${
                      isSelected
                        ? "bg-blue-600 border-blue-600 text-white"
                        : "bg-white border-gray-400 text-transparent"
                    }`}
                  >
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                  <OptionIcon option={option} />
                  <span className={`flex-1 min-w-0 truncate ${isSelected ? "text-blue-600" : "text-gray-900"}`}>
                    {option.name || option.value || "Unnamed option"}
                  </span>
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default SubcategoryFiltersPanel;
